/* Review highlights — automated selection from the shared Stamped client.
   Slots 1..n-1: top-by-helpful-votes passing the quality floor.
   Final slot: newest qualifying review not already shown.
   All content rendered via textContent; nothing fabricated outside the
   clearly-labeled design-mode path. */

/* Tap-to-expand: quotes clamp via CSS; tapping a truncated card shows
   the full text, tapping away (or Esc) collapses it. Applies to both
   automated and block-mode cards. */
window.bsRhlBindExpansion = window.bsRhlBindExpansion || function (scope) {
  var cards = (scope || document).querySelectorAll('.rhl-card');
  cards.forEach(function (card) {
    if (card.dataset.rhlExpandBound) return;
    card.dataset.rhlExpandBound = '1';
    var quote = card.querySelector('.rhl-card__quote');
    if (!quote) return;

    function truncated() {
      return quote.scrollHeight > quote.clientHeight + 2;
    }

    function mark() {
      card.classList.toggle('is-truncatable', truncated() || card.classList.contains('is-expanded'));
    }
    mark();
    window.addEventListener('resize', mark);

    function collapse() {
      card.classList.remove('is-expanded');
      card.setAttribute('aria-expanded', 'false');
      mark();
    }

    card.setAttribute('tabindex', '0');
    card.setAttribute('role', 'button');
    card.setAttribute('aria-expanded', 'false');

    function toggle() {
      if (!card.classList.contains('is-expanded') && !truncated()) return;
      var open = card.classList.toggle('is-expanded');
      card.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        document.querySelectorAll('.rhl-card.is-expanded').forEach(function (other) {
          if (other !== card) {
            other.classList.remove('is-expanded');
            other.setAttribute('aria-expanded', 'false');
          }
        });
      }
      mark();
    }

    card.addEventListener('click', toggle);
    card.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
      if (e.key === 'Escape') collapse();
    });
  });

  if (!document.body.dataset.rhlOutsideBound) {
    document.body.dataset.rhlOutsideBound = '1';
    document.addEventListener('click', function (e) {
      document.querySelectorAll('.rhl-card.is-expanded').forEach(function (card) {
        if (!card.contains(e.target)) {
          card.classList.remove('is-expanded');
          card.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }
};

document.addEventListener('DOMContentLoaded', function () {
  window.bsRhlBindExpansion(document);
});

if (typeof customElements.get('review-highlights') === 'undefined') {

  class ReviewHighlights extends HTMLElement {

    connectedCallback() {
      this.productId = this.dataset.productId;
      this.productIds = (this.dataset.productIds || this.dataset.productId || '')
        .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      this.selection = this.dataset.selection === 'newest' ? 'newest' : 'top';
      this.storeUrl = this.dataset.storeUrl;
      this.cardCount = Math.min(4, parseInt(this.dataset.cardCount || '3', 10));
      this.minRating = parseInt(this.dataset.minRating || '4', 10);
      this.minLength = parseInt(this.dataset.minLength || '40', 10);
      this.showNewPill = this.dataset.showNewPill === 'true';

      if (!this.productIds.length || !this.storeUrl || !window.BSStampedClient) {
        this._hide();
        return;
      }

      if (this.dataset.designMode === 'true') {
        this._renderDesignSample();
        return;
      }

      // Defer the fetch until the section approaches the viewport
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);
          this._load();
        }, { rootMargin: '400px 0px' }).observe(this);
      } else {
        this._load();
      }
    }

    _load() {
      const client = window.BSStampedClient;
      const ids = this.productIds;
      Promise.all([
        Promise.all(ids.map(id => client.getSummary(this.storeUrl, id))),
        Promise.all(ids.map(id => client.getReviews(this.storeUrl, id, { sort: 'newest', take: 10 })))
      ]).then(([summaries, newestLists]) => {
        const summary = this._mergeSummaries(summaries.filter(Boolean));
        const qualifies = r => r.rating >= this.minRating && (r.message || '').length >= this.minLength;

        const seen = new Set();
        const newestAll = [];
        newestLists.forEach(l => {
          if (!l) return;
          l.reviews.forEach(r => {
            if (seen.has(r.id)) return;
            seen.add(r.id);
            newestAll.push(r);
          });
        });
        newestAll.sort((a, b) => new Date(b.date) - new Date(a.date));

        let picks;
        let newestId = null;

        if (this.selection === 'newest') {
          picks = newestAll.filter(qualifies).slice(0, this.cardCount);
          if (this.showNewPill && picks[0]) newestId = picks[0].id;
        } else {
          const topPool = summary ? summary.topVoted.filter(qualifies) : [];
          topPool.sort((a, b) => b.votes - a.votes);
          picks = topPool.slice(0, this.cardCount - 1);
          const pickedIds = new Set(picks.map(r => r.id));
          const newestPick = newestAll.find(r => qualifies(r) && !pickedIds.has(r.id)) || null;
          if (newestPick) {
            picks.push(newestPick);
            newestId = newestPick.id;
          } else if (topPool[this.cardCount - 1]) {
            picks.push(topPool[this.cardCount - 1]);
          }
        }

        if (picks.length < 2) {
          this._hide();
          return;
        }

        this._renderHeader(summary);
        this._renderCards(picks, newestId);
        this.classList.add('is-ready');
        if (window.bsRhlBindExpansion) window.bsRhlBindExpansion(this);
      }).catch(() => this._hide());
    }

    _mergeSummaries(summaries) {
      if (!summaries.length) return null;
      if (summaries.length === 1) return summaries[0];
      const merged = { total: 0, rating: 0, stars: {}, topVoted: [] };
      let weighted = 0;
      summaries.forEach(s => {
        merged.total += s.total;
        weighted += s.rating * s.total;
        merged.topVoted = merged.topVoted.concat(s.topVoted);
        Object.keys(s.stars || {}).forEach(k => {
          merged.stars[k] = (merged.stars[k] || 0) + s.stars[k];
        });
      });
      merged.rating = merged.total ? weighted / merged.total : 0;
      return merged;
    }

    _hide() {
      this.classList.add('is-hidden');
      this.classList.add('is-ready'); // release the height reservation
    }

    _renderHeader(summary) {
      if (summary && summary.total > 0) {
        const eyebrow = this.querySelector('[data-rhl-eyebrow]');
        const count = this.querySelector('[data-rhl-count]');
        if (count) count.textContent = summary.total;
        if (eyebrow) eyebrow.hidden = false;
        const score = this.querySelector('[data-rhl-score]');
        const avg = this.querySelector('[data-rhl-average]');
        if (avg) avg.textContent = (Math.round(summary.rating * 10) / 10).toFixed(1);
        const fill = this.querySelector('[data-rhl-stars-fill]');
        if (fill) fill.style.width = Math.min(100, summary.rating / 5 * 100) + '%';
        const stars = this.querySelector('[data-rhl-stars]');
        if (stars) stars.setAttribute('aria-label', summary.rating + ' out of 5 stars');
        if (score) score.hidden = false;
      } else {
        // Cards loaded but the summary failed: plain heading instead
        const alt = this.querySelector('[data-rhl-alt-heading]');
        if (alt) alt.hidden = false;
      }
    }

    _renderCards(picks, newestId) {
      const wrap = this.querySelector('[data-rhl-cards]');
      if (!wrap) return;
      wrap.textContent = '';
      picks.forEach(r => {
        wrap.appendChild(this._buildCard(r, this.showNewPill && newestId !== null && r.id === newestId));
      });
    }

    _buildCard(r, isNew) {
      const card = document.createElement('article');
      card.className = 'rhl-card bs-card';

      if (isNew) {
        const pill = document.createElement('span');
        pill.className = 'rhl-card__new';
        pill.textContent = 'New';
        card.appendChild(pill);
      }

      const stars = document.createElement('p');
      stars.className = 'rhl-card__stars';
      stars.textContent = '★'.repeat(r.rating) + '☆'.repeat(5 - r.rating);
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', r.rating + ' out of 5 stars');
      card.appendChild(stars);

      const quote = document.createElement('blockquote');
      quote.className = 'rhl-card__quote';
      quote.textContent = '“' + (r.message || '').trim() + '”';
      card.appendChild(quote);

      const meta = document.createElement('p');
      meta.className = 'rhl-card__meta';
      meta.appendChild(document.createTextNode(this._formatAuthor(r.author)));
      if (r.verified) {
        meta.appendChild(document.createTextNode(' · '));
        const v = document.createElement('span');
        v.className = 'rhl-card__verified';
        v.textContent = 'Verified';
        meta.appendChild(v);
      }
      card.appendChild(meta);

      return card;
    }

    _formatAuthor(author) {
      const parts = (author || 'Anonymous').trim().split(/\s+/);
      if (parts.length === 1) return parts[0];
      return parts[0] + ' ' + parts[parts.length - 1][0] + '.';
    }

    _truncate(text, max) {
      if (!text || text.length <= max) return text || '';
      const cut = text.slice(0, max);
      const space = cut.lastIndexOf(' ');
      return (space > 40 ? cut.slice(0, space) : cut) + '…';
    }

    /* Theme editor: clearly-labeled sample, no network */
    _renderDesignSample() {
      this._renderHeader({ total: 128, rating: 4.8 });
      const eyebrow = this.querySelector('[data-rhl-eyebrow]');
      if (eyebrow) eyebrow.firstChild.textContent = 'Sample — from ';
      const samples = [
        { id: 1, rating: 5, message: 'Sample review card for the theme editor preview. Storefront cards render live Stamped reviews.', author: 'Sample A.', verified: true, votes: 3 },
        { id: 2, rating: 5, message: 'Sample review card for the theme editor preview. Storefront cards render live Stamped reviews.', author: 'Sample B.', verified: true, votes: 2 },
        { id: 3, rating: 4, message: 'Sample review card for the theme editor preview. Storefront cards render live Stamped reviews.', author: 'Sample C.', verified: false, votes: 0 }
      ].slice(0, this.cardCount);
      this._renderCards(samples, this.showNewPill ? 3 : null);
      const note = document.createElement('p');
      note.className = 'rhl__design-note';
      note.textContent = 'Theme editor: sample content shown. The storefront renders live Stamped reviews.';
      this.querySelector('[data-rhl-inner]').appendChild(note);
      this.classList.add('is-ready');
    }

  }

  customElements.define('review-highlights', ReviewHighlights);

}
